import reduce from "lodash/reduce";
import sortBy from "lodash/sortBy";
import {
  ICheckboxConfig,
  ICheckboxGroupConfig,
  ICheckboxGroupValue,
  IOption,
  IOptionGroup,
} from "component-library";

import { IFacet, IFacetGroup } from "shared/models/facet";
import { ICountry } from "shared/models/location";
import { ICheckboxGroupItemConfig } from "component-library/src/components/Organisms/CheckboxGroup/components/CheckboxGroupItem";

interface IMapFacetCheckboxGroupOptions {
  isCollapsible?: boolean;
  isExpandedByDefault?: boolean;
  unwrapGroupCodes?: string[];
  sort?: boolean;
  sortFacets?: boolean;
}

export const mapCountryOptions = (items: ICountry[] = []): IOption<string>[] =>
  items.map((item) => ({
    name: item.localisedCountryName,
    value: item.countryCode,
  }));

export const mapFacetOptions = (items: IFacet[] = []): IOption<number>[] =>
  sortBy(
    items.map((item) => ({ name: item.name, value: item.id })),
    [(item) => item.name]
  );

export const mapFacetGroupedOptions = (
  groups: IFacetGroup[] = []
): IOptionGroup<number>[] =>
  groups.map((item) => ({
    label: item.name,
    options: mapFacetOptions(item.facets),
  }));

export function mapFacetCheckboxGroup(
  items: (IFacetGroup | IFacet)[] = [],
  options?: IMapFacetCheckboxGroupOptions
): ICheckboxGroupItemConfig["options"] {
  const mappedItems = items.reduce((result, item) => {
    if ("facets" in item) {
      if (options?.unwrapGroupCodes?.includes(item.code)) {
        result.push(...mapFacetCheckboxGroup(item.facets, options));
        return result;
      }

      result.push({
        label: item.name,
        name: item.name,
        isCollapsible: options?.isCollapsible ?? false,
        isExpandedByDefault: options?.isExpandedByDefault,
        options: mapFacetCheckboxGroup(item.facets, options),
      });
      return result;
    }

    result.push({ label: item.name, value: String(item.id) });
    return result;
  }, [] as ICheckboxGroupConfig["options"]);

  return options?.sort ||
    (options?.sortFacets && mappedItems[0] && "value" in mappedItems[0])
    ? sortBy(mappedItems, [(subItem) => subItem.label])
    : mappedItems;
}

function isStringValues(
  values: boolean | (number | string)[]
): values is string[] {
  return (
    Array.isArray(values) && values.every((value) => typeof value === "string")
  );
}

export function mapCheckboxOptionsToValue(
  checkboxOptions: ICheckboxGroupConfig["options"] = [],
  values: boolean | (number | string)[] = false
): ICheckboxGroupValue {
  const isStringValue = isStringValues(values);
  return checkboxOptions.reduce((result, option) => {
    if ("value" in option) {
      result[option.value] =
        typeof values === "boolean"
          ? values
          : values.includes(
              isStringValue ? option.value : Number(option.value)
            );
    } else if ("options" in option) {
      result[option.name] = mapCheckboxOptionsToValue(option.options, values);
    }

    return result;
  }, {} as ICheckboxGroupValue);
}

export function mapCheckboxValueToList(values: ICheckboxGroupValue): number[];
export function mapCheckboxValueToList(
  values: ICheckboxGroupValue,
  options: { keepString?: boolean }
): string[];
export function mapCheckboxValueToList(
  values: ICheckboxGroupValue,
  options: { keepString?: boolean } = {}
): (number | string)[] {
  return reduce(
    values,
    (result, value, key) => {
      if (typeof value === "boolean") {
        return value
          ? [...result, options.keepString ? key : Number(key)]
          : result;
      }

      return [...result, ...mapCheckboxValueToList(value, options)];
    },
    []
  );
}

export function mapListToCheckboxValue(list: number[]): ICheckboxGroupValue {
  return (
    list?.reduce(
      (result, value) => ({ ...result, [value]: true }),
      {} as ICheckboxGroupValue
    ) ?? {}
  );
}

export function mapCountriesCheckboxGroup(
  items: ICountry[] = []
): ICheckboxConfig[] {
  return items.map((item) => ({
    value: item.countryCode,
    label: item.localisedCountryName,
  }));
}
